import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { TemplateRef } from '@angular/core';
import { ActionIconDef, GridConfig, GridRow } from '@inavista/components/grid';

// Parts order popup (Transactions → Create Parts Order, the parts orders list, Links) → Items on @inavista/components.
// Same columns, labels, order, default visibility, widths, alignment, formats, links, toolbar, row action and inline
// editing as the Syncfusion grid it replaces. The cells keep their editors (each column's `edit`, drawn by
// CellEditorHost), so the item list, obsolete / alternate item and quantity rules run as before. Rows are the
// popup's own `items` array (LocalRowsGridApi, no endpoint), saved with the popup's Save / Place Order, as before.

export const PARTS_ORDER_ITEMS_URL = 'page://parts-order-items';
export const PARTS_ORDER_ITEMS_PERSISTENCE_KEY = 'rims.service.parts-order-items';
export const ADD_ITEM_ACTION = 'add-item';
/** The row key: the row's index in the page's items array (the rows have no key of their own). Not a column. */
export const ITEM_ROW_KEY = '__row';
/** A hidden field the page writes when a row opens, so leaving it always saves it (the Syncfusion grid saved on leave). */
export const ITEM_OPEN_FIELD = '__open';

const BLACK = '#000000de';
const INACTIVE = '#cccccc';
const PLUS_ICON = '<line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />';
const TRASH_ICON = '<polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />';

const SQL_TYPES: Record<string, string> = { number: 'decimal', date: 'datetime', string: 'nvarchar' };

/** The fields the grid shows: every column but the Actions command column. */
export function itemFields(columns: any[]): any[] {
  return columns.filter(c => c.field && !c.commands);
}

/** Hidden and kept out of the column chooser: a backing field (the line number), never offered. */
function isBacking(c: any) {
  return c.visible === false && c.showInColumnChooser === false;
}

/** The schema of the rows, from the Syncfusion column list, plus the open-row marker. */
export function itemsSchema(columns: any[]): [string, string][] {
  return [...itemFields(columns).map(c => [c.field, SQL_TYPES[c.type] ?? 'nvarchar'] as [string, string]), [ITEM_OPEN_FIELD, 'nvarchar']];
}

/** The editable cells: the columns with a cell editor (CellEditorHost), and those Syncfusion edited with its own text box. */
export function itemEditFields(columns: any[]): { hosted: string[]; builtIn: string[] } {
  const fields = itemFields(columns).filter(c => !isBacking(c) && !c.isPrimaryKey);
  return {
    hosted: fields.filter(c => c.edit).map(c => c.field),
    builtIn: fields.filter(c => !c.edit && c.allowEditing !== false).map(c => c.field),
  };
}

// Syncfusion's 'n' (en-US: #,##0.###) and 'd MMM, y'.
const N_FORMAT: Intl.NumberFormatOptions = { maximumFractionDigits: 3 };
const DATE_FORMAT = 'd MMM, y';

export interface ItemsGridContext {
  /** The Syncfusion column list (getGridColumns). */
  columns: any[];
  /** Inline editing on: the old editSettings.allowEditing (order Open, not a manager, not read-only). */
  canEdit: boolean;
  /** Add Item showed for this role claim only (PO_Popup_ItemTabGrid_Toolbar_Add_Item). */
  canAdd: boolean;
  canExport: boolean;
  editors: Record<string, TemplateRef<unknown>>;
  editLockReason: () => string | null;
  addDisabledReason: () => string | null;
}

/** The old Delete Item command, greyed (disabled-grid-button) with its reason. */
export function buildItemsActionIcons(deleteLockReason: () => string | null): ActionIconDef[] {
  return [{
    key: 'Delete Item', label: 'Delete Item', icon: TRASH_ICON,
    colorFn: () => (deleteLockReason() ? INACTIVE : BLACK),
    disabledFn: () => !!deleteLockReason(),
    labelFn: () => deleteLockReason() ?? 'Delete Item',
  }];
}

export function buildItemsGridConfig(ctx: ItemsGridContext): GridConfig {
  const fields = itemFields(ctx.columns);
  const shown = fields.filter(c => !isBacking(c));
  const { hosted, builtIn } = itemEditFields(ctx.columns);
  const numbers = shown.filter(c => c.type === 'number').map(c => c.field);
  const dates = shown.filter(c => c.type === 'date').map(c => c.field);
  return {
    apiBaseUrl: PARTS_ORDER_ITEMS_URL,
    clientSide: true,
    keyColumn: ITEM_ROW_KEY,
    // 30, as the other Service Order popup grids.
    rowHeight: 30,
    defaultColumnOrder: shown.map(c => c.field),
    hiddenByDefaultColumns: [...fields.filter(isBacking).map(c => c.field), ITEM_OPEN_FIELD],
    startHiddenColumns: shown.filter(c => c.visible === false).map(c => c.field),
    // The old grid kept these out of its column chooser while showing them.
    alwaysVisibleColumns: shown.filter(c => c.showInColumnChooser === false && c.visible !== false).map(c => c.field),
    columnLabels: Object.fromEntries(shown.map(c => [c.field, c.headerText])),
    // The old widths; Description had none and took the space left, so it is the only one that stretches.
    columnWidths: Object.fromEntries(shown.map(c => [c.field, c.width ? +c.width : 300])),
    stretchColumns: true,
    columnSizing: Object.fromEntries(shown.filter(c => c.width).map(c => [c.field, { noStretch: true }])),
    // Text, dates and headers left; Ord Qty, Ship Qty and Rcvd Qty right, as before.
    columnAlign: Object.fromEntries(shown.map(c => [c.field, {
      horizontal: (c.textAlign ?? 'Left').toLowerCase() as 'left' | 'right' | 'center', header: { horizontal: 'left' as const },
    }])),
    columnTypes: { ...Object.fromEntries(numbers.map(f => [f, 'decimal' as const])), ...Object.fromEntries(dates.map(f => [f, 'date' as const])) },
    columnNumberFormats: Object.fromEntries(numbers.map(f => [f, N_FORMAT])),
    columnDateFormats: Object.fromEntries(dates.map(f => [f, DATE_FORMAT])),
    // Item (checkbox filter) and the Excel-filter columns.
    excelFilterColumns: shown.filter(c => c.filter?.type === 'Excel' || c.filter?.type === 'CheckBox').map(c => c.field),
    // Syncfusion's default: Ctrl+click adds a sort level.
    multiSort: true,
    // Item and Description open the item; Shipment ID opens the shipment order, as before.
    hyperlinkColumns: ['itemCode', 'itemDescription', 'shipmentOrderLN'],
    // The old toolbar: Show/Hide Columns, Add Item (role claim), Search, Excel export (AllGrid_Toolbar_Excel_Export),
    // Clear filters. Reset layout is kept: it undoes the saved layout.
    enableColumnsPicker: true,
    enableExport: ctx.canExport,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    toolbarActions: ctx.canAdd ? [{
      key: ADD_ITEM_ACTION, label: 'Add Item', kind: 'button', icon: PLUS_ICON, position: 'start',
      disabled: () => !!ctx.addDisabledReason(), disabledReason: () => ctx.addDisabledReason() ?? '',
    }] : [],
    // Editing as the old editSettings: one click on a cell opens its row (the old mouseup handler), only while the
    // order is Open and for a user who may change it.
    editMode: ctx.canEdit ? 'inline' : 'none',
    editSaveMode: 'row',
    editTrigger: 'click',
    editableColumns: [...hosted, ...builtIn],
    editorTemplates: ctx.editors,
    readOnlyCellFn: () => !!ctx.editLockReason(),
    // The old header's red * on Item: the marker only (validationRules required: false); validate() decides on Save.
    // Also read-only (the old header showed it either way).
    requiredColumns: ['itemCode'],
    requiredMarkers: 'always',
    deleteMode: 'host',
    rowDeletable: () => false,
    emptyStateMessage: 'No records to display',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: PARTS_ORDER_ITEMS_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}

/** The page row a grid row stands for. */
export function itemRowKey(row: GridRow): number {
  return row?.[ITEM_ROW_KEY] as number;
}
